import type {
  Blog,
  HistoryMessage,
  Page,
  Product,
  ProviderPayload,
  ReferencedEntry,
  ReferencedProduct,
  SessionHistoryResponse,
} from '@/types/chat';
import { makeWelcomeMessage, type ChatMessage } from './messages';

/**
 * 历史接口顶层的 `referenced` 是按 slug 索引的扁平 map，产品 / 博客 /
 * 页面混在一起靠 `kind` 区分。这里把它还原成实时路径用的 ProviderPayload
 * 三数组结构，渲染层因此完全不需要知道两种形状的存在。
 *
 * 只挑渲染需要的字段：产品详情页（ProductPreviewPane）要显示的 description /
 * tabs / 成分 / 用法 / 注意事项照实时路径原样保留；模型侧的 price /
 * availability / contenance / gist / other_formats 一律丢弃。
 *
 * 每个字段都按可选处理——老 session 存的是旧快照，缺 id / variants /
 * families / image 是常态，见 spec §2.5。
 */
export function adaptReferenced(
  referenced: Record<string, ReferencedEntry> | undefined,
): ProviderPayload {
  const payload: ProviderPayload = { products: [], blogs: [], pages: [] };
  if (!referenced) return payload;

  for (const entry of Object.values(referenced)) {
    if (!entry || typeof entry !== 'object') continue;

    if (entry.kind === 'product') {
      payload.products.push(toProduct(entry));
    } else if (entry.kind === 'blog') {
      payload.blogs.push(toBlog(entry));
    } else if (entry.kind === 'page') {
      payload.pages.push(toPage(entry));
    }
  }

  return payload;
}

function toProduct(entry: ReferencedProduct): Product {
  return {
    // id 原样透传：运行时是 number，实时路径也是 number。转成 string 会让
    // 宿主的 onAddToCart 在「新回复」和「恢复的历史」上收到不同类型的值。
    id: entry.id as Product['id'],
    title: entry.title,
    slug: entry.slug,
    url: entry.url,
    foreword: entry.foreword ?? '',
    effects: entry.effects ?? [],
    families: entry.families ?? [],
    variants: entry.variants ?? [],
    ...(Object.fromEntries(SHEET_FIELDS.filter((k) => entry[k]).map((k) => [k, entry[k]])) as Partial<Product>),
  };
}

/** What the product sheet shows, kept as the live payload sends it. */
const SHEET_FIELDS = [
  'chineseTitle',
  'pinyinTitle',
  'description',
  'tabs',
  'composition_summary',
  'ingredients_list',
  'usages',
  'warnings',
  'images',
] as const;

function toBlog(entry: Extract<ReferencedEntry, { kind: 'blog' | 'page' }>): Blog {
  return {
    title: entry.title,
    slug: entry.slug,
    url: entry.url,
    ...(entry.image ? { image: entry.image } : {}),
    ...(entry.description ? { description: entry.description } : {}),
    ...(entry.blogFamilySlug ? { blogFamilySlug: entry.blogFamilySlug } : {}),
  };
}

function toPage(entry: Extract<ReferencedEntry, { kind: 'blog' | 'page' }>): Page {
  return {
    title: entry.title,
    slug: entry.slug,
    url: entry.url,
    ...(entry.description ? { description: entry.description } : {}),
  };
}

const EMPTY_PAYLOAD: ProviderPayload = { products: [], blogs: [], pages: [] };

/**
 * 把历史接口的响应还原成可渲染的消息流。
 *
 * payload 单独返回而不塞进每条 assistant 消息：ChatWidget 本来就把所有
 * assistant 消息的 provider_payload 合并成一份 session 级 payload，
 * 后端也只给一份 session 级 referenced，硬拆到每条消息上没有意义。
 *
 * welcome 气泡是纯客户端概念，后端不存，这里补在最前面。
 */
export function hydrateSession(response: SessionHistoryResponse): {
  messages: ChatMessage[];
  payload: ProviderPayload;
} {
  // 无法辨认的 200 不是「这个会话没有消息」——照原样渲染会让用户看到一段空
  // 对话，而 sid 还在，下一轮回复就会带上他看不见的上下文。调用方
  // （useChatSession 的恢复 catch）已经会把这里的 throw 变成一条可见、
  // 可重试的失败，这正是我们想要的结果。
  if (!response || typeof response !== 'object' || !Array.isArray(response.messages)) {
    throw new TypeError('Malformed session history envelope');
  }

  const ordered = [...response.messages].sort((a, b) => a.sequence_no - b.sequence_no);

  const messages: ChatMessage[] = [makeWelcomeMessage()];
  for (const message of ordered) {
    // role 显式分支，不用 `=== 'user' ? … : …` 的兜底写法——后者会把任何
    // 非 'user' 的值（未来可能出现的 system/tool）都当成 assistant 渲染，
    // 连带挂上反馈组件，把不是真正助手回复的消息变成能点赞点踩的气泡。
    // 类型上 HistoryMessage.role 目前只声明 'user' | 'assistant'，但那只是
    // 编译期收窄——这里处理的是运行时从后端解析出来的 JSON，不受它约束。
    if (message.role === 'user') {
      messages.push(toUserMessage(message));
    } else if (message.role === 'assistant') {
      messages.push(toAssistantMessage(message, response.session_id));
    }
  }

  const payload = adaptReferenced(response.referenced);
  // MessageBubble 只从 per-message provider_payload 里读 blogs（见其
  // `message.response.provider_payload.blogs`），而后端只给一份 session
  // 级 referenced，没法还原到具体某一轮。挂在最后一条 assistant 消息上：
  // 会话里出现一次卡片轨，而不是每轮重复一份，也不是像之前那样整块消失
  // （P1-5）。EMPTY_PAYLOAD 是共享的模块级常量，这里只读它、展开成新对象，
  // 绝不直接修改它本身。
  if (payload.blogs.length > 0) {
    const lastAssistantIndex = findLastIndex(messages, (m) => m.type === 'assistant');
    const lastAssistant = lastAssistantIndex === -1 ? undefined : messages[lastAssistantIndex];
    if (lastAssistant && lastAssistant.type === 'assistant') {
      messages[lastAssistantIndex] = {
        ...lastAssistant,
        response: {
          ...lastAssistant.response,
          provider_payload: { ...EMPTY_PAYLOAD, blogs: payload.blogs },
        },
      };
    }
  }

  return { messages, payload };
}

// `Array.prototype.findLastIndex` isn't available under this project's
// ES2020 lib target — same reason `.at()` is banned elsewhere.
function findLastIndex<T>(items: T[], predicate: (item: T) => boolean): number {
  for (let i = items.length - 1; i >= 0; i -= 1) {
    if (predicate(items[i])) return i;
  }
  return -1;
}

function toUserMessage(message: HistoryMessage): ChatMessage {
  return {
    id: message.message_id,
    type: 'user',
    createdAt: message.created_at,
    text: message.content,
  };
}

function toAssistantMessage(message: HistoryMessage, sessionId: string): ChatMessage {
  return {
    id: message.message_id,
    type: 'assistant',
    createdAt: message.created_at,
    response: {
      session_id: sessionId,
      reply: message.content,
      structured_reply: message.structured_reply,
      assistant_message: {
        message_id: message.message_id,
        role: 'assistant',
        content: message.content,
        turn_no: message.turn_no,
        sequence_no: message.sequence_no,
        created_at: message.created_at,
      },
      // 历史接口不返回这个字段，紧急短路消息恢复后会退化成普通样式。
      // red_flags / emergency_cta 这些 section 本身在 structured_reply 里，照常渲染。
      is_redline_short_circuit: false,
      // session 级 payload 由 hydrateSession 单独返回，这里留空。
      provider_payload: EMPTY_PAYLOAD,
    },
  };
}
